import { useRef, useState } from 'react';
import { ErrorType } from './constants';
import {
  SurveyError,
  SurveyErrorGroup,
  SurveyErrorType,
  SurveyErrorMeta,
  UseSurveyErrorsProps,
  UseSurveyErrorsReturn,
  SurveyErrorMetaGroup,
} from './types';

const defaultErrorGroups: SurveyErrorGroup = {
  redirect: [ErrorType.REDIRECT, ErrorType.COLLECTOR_CLOSED, ErrorType.REQUEST_IP_DISALLOWED],
  notFound: [ErrorType.FOUR_OH_FOUR],
  validation: [ErrorType.MISSING_REQUIRED_FIELD, ErrorType.INVALID_OPEN_ENDED_VALIDATION, ErrorType.FAILED_VALIDATION],
  canned: [ErrorType.SURVEY_CANNED],
  gone: [ErrorType.INVALID_SURVEY_ID, ErrorType.INVALID_RESPONSE_ID, ErrorType.INVALID_COLLECTOR_ID],
  badRequest: [
    ErrorType.MISSING_OR_MALFORMED_COLLECTOR_ID,
    ErrorType.MISSING_OR_MALFORMED_SURVEY_ID,
    ErrorType.MISSING_OR_MALFORMED_RESPONSE_ID,
  ],
  /**  Added so it's not caught by unkownErrors. which was making the 500 error display when not needed. */
  invalidSurveyVersion: [ErrorType.INVALID_SURVEY_VERSION],
};

/**
 * Module variable outside of react lifecycle, to work around update delay issues - this is the source of truth
 * Temp-workaround - these issues will go away when errors are in Redux store.
 */
let errorsWithoutState: SurveyError[] = [];

function useSurveyErrors(
  config: UseSurveyErrorsProps = { groups: defaultErrorGroups, meta: {} }
): UseSurveyErrorsReturn {
  const [errors, setErrors] = useState<SurveyError[]>([]);
  const questionOrder = useRef<string[]>([]);

  // re-sync on each render
  errorsWithoutState = errors;

  /** Helper to sort by `questionOrder` (if it exists and both questionIds exist in it) */
  const sortByQuestionOrder = ({ questionId: a }: SurveyError, { questionId: b }: SurveyError): number => {
    const order = questionOrder.current;
    if (!order || !a || !b || !order.includes(a) || !order.includes(b)) {
      return 0;
    }
    return order.indexOf(a) - order.indexOf(b);
  };

  const errorGroups = config.groups || {};
  let metaConfig = config.meta ?? {};

  /** Set error metadata */
  const setMeta = (meta: SurveyErrorMetaGroup): void => {
    metaConfig = { ...config.meta, ...meta };
  };

  /** Add an error to state */
  const setError = (error: SurveyError): void => {
    errorsWithoutState = [...errorsWithoutState, error];
    setErrors(errs => [...errs, error].sort(sortByQuestionOrder));
  };

  /** Reset all errors assigned to a question  */
  const setErrorsById = (id: string | (string | null)[] | null, newErrors: SurveyError[]): void => {
    const ids = Array.isArray(id) ? id : [id];
    errorsWithoutState = [...errorsWithoutState.filter(e => !ids.includes(e.questionId)), ...newErrors].sort(
      sortByQuestionOrder
    );
    setErrors(errorsWithoutState);
  };

  /** Remove errors by questionId */
  const removeErrorsById = (id: string | (string | null)[] | null): void => {
    const ids = Array.isArray(id) ? id : [id];
    errorsWithoutState = errorsWithoutState.filter(e => !ids.includes(e.questionId));
    setErrors(errs => errs.filter(e => !ids.includes(e.questionId)));
  };

  /** Get errors by questionId */
  const getErrorsById = (id: string): SurveyError[] => {
    return errorsWithoutState.filter(e => e.questionId === id);
  };

  /** Get errors by type/code */
  const getErrorsByType = (type: SurveyErrorType | SurveyErrorType[]): SurveyError[] => {
    const types = Array.isArray(type) ? type : [type];
    return errorsWithoutState.filter(e => types.includes(e.code));
  };

  /** Remove errors by type/code */
  const removeErrorsByType = (type: SurveyErrorType | SurveyErrorType[]): void => {
    const types: SurveyErrorType[] = Array.isArray(type) ? type : [type];
    errorsWithoutState = errorsWithoutState.filter(e => !types.includes(e.code)).sort(sortByQuestionOrder);
    setErrors(errorsWithoutState);
  };

  /** Get Meta info from an error */
  const getErrorMetaByType = (type: SurveyErrorType | SurveyErrorType[]): SurveyErrorMeta | null => {
    const errorType = getErrorsByType(type)[0];
    if (errorType) {
      const defaultMeta = metaConfig[errorType.code] ?? {};
      return { ...defaultMeta, ...errorType.meta };
    }
    return null;
  };

  /** reset (clear) errors */
  const reset = (): void => setErrors([]);

  const combinedSetErrors: React.Dispatch<React.SetStateAction<SurveyError[]>> = value => {
    errorsWithoutState = (typeof value === 'function' ? value(errors || []) : value).sort(sortByQuestionOrder);
    setErrors(errorsWithoutState);
  };

  // API
  return {
    errors: errorsWithoutState,
    errorGroups,
    get hasNoErrors(): boolean {
      return !errors.length;
    },
    get hasVersionChanged(): boolean {
      return errors.some(({ code }: SurveyError) => code === ErrorType.INVALID_SURVEY_VERSION);
    },
    get hasNotFoundErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.notFound.includes(code));
    },
    get hasRedirectErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.redirect.includes(code));
    },
    get hasValidationErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.validation.includes(code));
    },
    get hasGoneErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.gone.includes(code));
    },
    get hasCannedError(): boolean {
      return errors.some(({ code }: SurveyError) => code === ErrorType.SURVEY_CANNED);
    },
    get hasBadRequestErrors(): boolean {
      return errors.some(({ code }: SurveyError) => errorGroups.badRequest.includes(code));
    },
    get hasUnknownErrors(): boolean {
      return errors.some(({ code }) => !Object.values(errorGroups).flat().includes(code));
    },
    setQuestionOrder: newQuestionOrder => {
      questionOrder.current = newQuestionOrder;
      combinedSetErrors(errorsWithoutState);
    },
    reset,
    setErrors: combinedSetErrors,
    setError,
    setErrorsById,
    getErrorsByType,
    getErrorMetaByType,
    getErrorsById,
    removeErrorsByType,
    removeErrorsById,
    setMeta,
  } as UseSurveyErrorsReturn;
}

export default useSurveyErrors;
